import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountantDashboard = () => {
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [terms, setTerms] = useState([]);
  const [years, setYears] = useState([]);

  const fetchTermsAndYears = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accountant/meta`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch finance meta');
      }

      const data = await response.json();
      const nextTerms = Array.isArray(data.terms) ? data.terms : ['Term 1', 'Term 2', 'Term 3'];
      const nextYears = Array.isArray(data.years) ? data.years : [data.academic_year || new Date().getFullYear().toString()];

      setTerms(nextTerms.map((term) => ({ id: term, name: term })));
      setYears(nextYears);
      setSelectedTerm(data.active_term || nextTerms[0] || '');
      setSelectedYear(data.academic_year || nextYears[0] || '');
    } catch (error) {
      console.error('Error fetching finance metadata:', error);
      setTerms([
        { id: 'Term 1', name: 'Term 1' },
        { id: 'Term 2', name: 'Term 2' },
        { id: 'Term 3', name: 'Term 3' }
      ]);
      setYears([new Date().getFullYear().toString()]);
      setSelectedTerm('Term 1');
      setSelectedYear(new Date().getFullYear().toString());
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/accountant/dashboard?termId=${selectedTerm}&academicYear=${selectedYear}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setDashboardData(data || {});
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      setDashboardData({});
    } finally {
      setLoading(false);
    }
  }, [selectedTerm, selectedYear]);

  useEffect(() => {
    fetchTermsAndYears();
  }, [fetchTermsAndYears]);

  useEffect(() => {
    if (selectedTerm && selectedYear) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [selectedTerm, selectedYear, fetchDashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Finance Dashboard</h1>
          <p className="text-gray-600 mt-2">Term financial summary and performance metrics</p>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Term</label>
            <Select value={selectedTerm} onValueChange={setSelectedTerm}>
              <SelectTrigger>
                <SelectValue placeholder="Select Term" />
              </SelectTrigger>
              <SelectContent>
                {terms.map(term => (
                  <SelectItem key={term.id} value={term.id}>{term.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year</label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger>
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Fees Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Fees Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${dashboardData?.totalFeesPaid?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                of ${dashboardData?.expectedFees?.toFixed(2) || '0.00'} expected
              </p>
            </CardContent>
          </Card>

          {/* Balance Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Fee Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                ${dashboardData?.feesBalance?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-gray-500 mt-1">Outstanding amount</p>
            </CardContent>
          </Card>

          {/* Expenses Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Expenses Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${dashboardData?.totalExpensesPaid?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                of ${dashboardData?.expectedExpenses?.toFixed(2) || '0.00'} planned
              </p>
            </CardContent>
          </Card>

          {/* Balance Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Current Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                ${dashboardData?.currentBalance?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-gray-500 mt-1">Liquidity status</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Income Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Income Breakdown</CardTitle>
              <CardDescription>School fees, trips, groceries, uniforms</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'School Fees', value: dashboardData?.totalFeesPaid || 0 },
                      { name: 'Trip Fees', value: dashboardData?.tripCollections || 0 },
                      { name: 'Grocery Cash', value: dashboardData?.groceryCashTotal || 0 }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: $${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Expense Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Expense Summary</CardTitle>
              <CardDescription>Teacher salaries, statutory charges, operations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Teacher Salaries (3 months)</span>
                  <span className="font-semibold">${dashboardData?.salaryTotals?.total?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">ZIMRA Charges</span>
                  <span className="font-semibold">${dashboardData?.zimraTotal?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">NSSA/NASSA Charges</span>
                  <span className="font-semibold">${dashboardData?.nssaTotal?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Trip Expenses</span>
                  <span className="font-semibold">${dashboardData?.tripExpenses?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="border-t pt-4 flex justify-between">
                  <span className="text-sm font-bold">Total Expenses</span>
                  <span className="font-bold">${dashboardData?.totalExpensesPaid?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Salary Months */}
        <Card>
          <CardHeader>
            <CardTitle>Teacher Salary Breakdown by Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map(month => (
                <div key={month} className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Month {month}</h4>
                  <p className="text-2xl font-bold text-blue-600">
                    ${dashboardData?.salaryTotals?.[`month${month}`]?.toFixed(2) || '0.00'}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AccountantDashboard;
